import { useEffect, useState } from 'react';
import {
  Box, Button, FormHelperText, Grid, IconButton, MenuItem, Paper, TextField,
  ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';

import ConfirmModal from '../../modals/ConfirmModal';
import {
  DIAS_SEMANA, horarioVacio, slotInvalido, opcionesHora, ordenarDias, todosIguales,
  resumenHorarios, resumenDias
} from './disponibilidad';

const OPCIONES_INICIO = opcionesHora('inicio');
const OPCIONES_FIN = opcionesHora('fin');

// Atajos para elegir varios días de una vez
const ATAJOS = [
  { id: 'todos', etiqueta: 'Todos los días', dias: DIAS_SEMANA },
  { id: 'lunesAViernes', etiqueta: 'Lunes a viernes', dias: DIAS_SEMANA.slice(0, 5) },
  { id: 'finDeSemana', etiqueta: 'Fines de semana', dias: DIAS_SEMANA.slice(5) },
];

const mismoConjunto = (a, b) => a.length === b.length && a.every(d => b.includes(d));
const clonar = (slots) => slots.map(s => ({ ...s }));

// Botón seleccionado: relleno azul con texto blanco, imposible de confundir con uno sin elegir
const estiloSeleccionado = {
  '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
};

const FilaHorario = ({ slot, numero, onCambiar, onQuitar, puedeQuitar }) => {
  const invalido = slotInvalido(slot);
  return (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid sx={{ display: { xs: 'none', sm: 'block' } }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: 'text.secondary' }}>{numero}</Typography>
        </Grid>
        <Grid size={{ xs: 5, sm: 4.5 }}>
          <TextField
            select fullWidth size="small" label="Inicio" error={invalido}
            value={slot.inicio} onChange={(e) => onCambiar('inicio', e.target.value)}
          >
            {OPCIONES_INICIO.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 5, sm: 4.5 }}>
          <TextField
            select fullWidth size="small" label="Fin" error={invalido}
            value={slot.fin} onChange={(e) => onCambiar('fin', e.target.value)}
          >
            {OPCIONES_FIN.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid size={{ xs: 2, sm: 1.5 }}>
          <IconButton
            aria-label="Quitar horario"
            onClick={onQuitar}
            disabled={!puedeQuitar}
            sx={{
              bgcolor: (t) => alpha(t.palette.error.main, 0.1), color: 'error.oscuro', borderRadius: '8px',
              '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.2) }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Grid>
      </Grid>
      {invalido && (
        <FormHelperText error sx={{ ml: 0.5, mt: 0.5 }}>
          La hora de fin debe ser posterior a la de inicio (máximo 12:00 am).
        </FormHelperText>
      )}
    </Box>
  );
};

// Disponibilidad semanal del servicio. Se elige qué días atiende y, por defecto, UN solo horario
// que aplica a todos los días elegidos; si hace falta, se puede personalizar día por día.
// Componente controlado: value = [{ day, slots: [{ inicio, fin }] }].
const DisponibilidadSemanal = ({ value, onChange, error, onAviso }) => {
  const dias = ordenarDias(value || []);
  const seleccionados = dias.map(d => d.day);
  const [porDia, setPorDia] = useState(() => !todosIguales(value || []));
  const [expandidos, setExpandidos] = useState([]);
  const [confirmarUnificar, setConfirmarUnificar] = useState(false);

  // "Mismo horario" no puede mostrar días con horarios distintos (p. ej. al cargar un servicio
  // para editarlo): en ese caso se pasa a "por día"
  useEffect(() => {
    if (!porDia && !todosIguales(value || [])) setPorDia(true);
  }, [value, porDia]);

  const avisar = (mensaje, severidad = 'success') => { if (onAviso) onAviso(mensaje, severidad); };

  // Horario de partida para días nuevos: el del primer día elegido (o uno en blanco)
  const slotsBase = () => (dias[0]?.slots?.length ? dias[0].slots : [horarioVacio()]);

  // Reemplaza la selección de días; los que ya estaban conservan su horario y los nuevos copian el base
  const fijarDias = (nuevos) => {
    const base = slotsBase();
    onChange(ordenarDias(nuevos.map(day => dias.find(d => d.day === day) || { day, slots: clonar(base) })));
  };
  const alternarDia = (day) => fijarDias(
    seleccionados.includes(day) ? seleccionados.filter(d => d !== day) : [...seleccionados, day]
  );

  // ---- Modo "mismo horario": un solo juego de horarios que se escribe en todos los días ----
  const slotsComunes = slotsBase();
  const editarComunes = (slots) => onChange(dias.map(d => ({ ...d, slots: clonar(slots) })));
  const cambiarComun = (i, campo, valor) =>
    editarComunes(slotsComunes.map((s, j) => (j === i ? { ...s, [campo]: valor } : s)));

  // ---- Modo "por día" ----
  const editarDia = (day, fn) => onChange(dias.map(d => (d.day === day ? { ...d, slots: fn(d.slots || []) } : d)));
  const cambiarSlot = (day, i, campo, valor) =>
    editarDia(day, slots => slots.map((s, j) => (j === i ? { ...s, [campo]: valor } : s)));
  const quitarSlot = (day, i) => {
    const slots = dias.find(d => d.day === day)?.slots || [];
    // Quitar el último horario de un día equivale a dejar de atender ese día
    if (slots.length <= 1) return fijarDias(seleccionados.filter(d => d !== day));
    editarDia(day, s => s.filter((_, j) => j !== i));
  };
  const alternarExpandido = (day) =>
    setExpandidos(prev => (prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]));

  const replicarAlSiguiente = (day) => {
    const origen = dias.find(d => d.day === day);
    const inicio = DIAS_SEMANA.indexOf(day);
    let siguiente = null;
    for (let k = 1; k < DIAS_SEMANA.length && !siguiente; k++) {
      const candidato = DIAS_SEMANA[(inicio + k) % DIAS_SEMANA.length];
      if (seleccionados.includes(candidato)) siguiente = candidato;
    }
    if (!siguiente) return avisar('No hay otro día seleccionado para replicar', 'warning');
    onChange(dias.map(d => (d.day === siguiente ? { ...d, slots: clonar(origen.slots || []) } : d)));
    avisar(`Horarios replicados a ${siguiente}`);
  };

  const aplicarATodos = (day) => {
    const origen = dias.find(d => d.day === day);
    onChange(dias.map(d => ({ ...d, slots: clonar(origen.slots || []) })));
    avisar(`Horario de ${day} aplicado a los ${dias.length} días`);
  };

  // ---- Cambio de modo ----
  const cambiarModo = (_, modo) => {
    if (!modo) return; // clic sobre el modo que ya está activo
    if (modo === 'dia') return setPorDia(true);
    // Volver a "mismo horario" borra las diferencias entre días: si las hay, se pide confirmación
    if (todosIguales(dias)) return setPorDia(false);
    setConfirmarUnificar(true);
  };
  const unificar = () => {
    editarComunes(slotsComunes);
    setPorDia(false);
    setConfirmarUnificar(false);
  };

  const resumenComun = resumenHorarios(slotsComunes);

  return (
    <Box>
      <Typography variant="body2" color={error ? 'error.oscuro' : 'primary'} fontWeight={600} sx={{ mb: 1 }}>
        Disponibilidad Semanal<Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
      </Typography>
      {error && <FormHelperText error sx={{ mb: 1 }}>{error}</FormHelperText>}
      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
        La hora de <b>fin</b> debe ser posterior a la de <b>inicio</b>. El máximo es <b>12:00 am</b> (medianoche); no se permiten horarios que crucen al día siguiente.
      </Typography>

      {/* Días: atajos para elegir varios de una vez + un botón por día */}
      <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>¿Qué días atiendes?</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {ATAJOS.map(a => (
          <Button
            key={a.id}
            type="button"
            size="small"
            variant={mismoConjunto(seleccionados, a.dias) ? 'contained' : 'outlined'}
            onClick={() => fijarDias(a.dias)}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            {a.etiqueta}
          </Button>
        ))}
        {seleccionados.length > 0 && (
          <Button type="button" size="small" onClick={() => fijarDias([])} sx={{ textTransform: 'none' }}>
            Quitar todos
          </Button>
        )}
      </Box>

      <Grid container spacing={1} sx={{ mb: 1 }}>
        {DIAS_SEMANA.map(day => (
          <Grid key={day} size={{ xs: 6, sm: 3, md: 1.7 }}>
            <ToggleButton
              value={day}
              selected={seleccionados.includes(day)}
              onChange={() => alternarDia(day)}
              fullWidth
              sx={{ textTransform: 'none', fontWeight: 600, py: 1.25, borderRadius: '8px', ...estiloSeleccionado }}
            >
              {day}
            </ToggleButton>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
        {seleccionados.length === 0
          ? 'Selecciona al menos un día'
          : `${seleccionados.length} ${seleccionados.length === 1 ? 'día seleccionado' : 'días seleccionados'}`}
      </Typography>

      {/* Horario */}
      {dias.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'transparent', borderStyle: 'dashed' }}>
          <AccessTimeIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
          <Typography variant="body2">Elige los días en que atiendes para definir tu horario</Typography>
        </Paper>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
            <Typography variant="subtitle2" color="primary">¿En qué horario?</Typography>
            {dias.length > 1 && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={porDia ? 'dia' : 'mismo'}
                onChange={cambiarModo}
                aria-label="Cómo definir el horario"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <ToggleButton value="mismo" sx={{ flex: 1, textTransform: 'none', ...estiloSeleccionado }}>
                  Mismo horario todos los días
                </ToggleButton>
                <ToggleButton value="dia" sx={{ flex: 1, textTransform: 'none', ...estiloSeleccionado }}>
                  Horario distinto por día
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>

          {!porDia ? (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '8px' }}>
              <Typography variant="body1" fontWeight={600} color="primary">Horario de atención</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                Aplica a: {resumenDias(seleccionados)}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                {slotsComunes.map((slot, i) => (
                  <FilaHorario
                    key={i}
                    slot={slot}
                    numero={i + 1}
                    onCambiar={(campo, valor) => cambiarComun(i, campo, valor)}
                    onQuitar={() => editarComunes(slotsComunes.filter((_, j) => j !== i))}
                    puedeQuitar={slotsComunes.length > 1}
                  />
                ))}
              </Box>

              <Button
                type="button"
                startIcon={<AddIcon />}
                onClick={() => editarComunes([...slotsComunes, horarioVacio()])}
                sx={{ textTransform: 'none' }}
              >
                Agregar otro horario
              </Button>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                Úsalo si tienes turno partido, por ejemplo 9:00 am – 2:00 pm y 4:00 pm – 8:00 pm.
              </Typography>

              {resumenComun && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {resumenDias(seleccionados)} · {resumenComun}
                  </Typography>
                </Box>
              )}
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {dias.map(({ day, slots = [] }) => {
                const abierto = expandidos.includes(day);
                return (
                  <Paper key={day} variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <Box
                      onClick={() => alternarExpandido(day)}
                      sx={{
                        p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer',
                        bgcolor: abierto ? ((t) => alpha(t.palette.primary.main, 0.02)) : 'transparent'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <AccessTimeIcon color="primary" />
                        <Box>
                          <Typography variant="body1" fontWeight={600} color="primary">{day}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {resumenHorarios(slots) || 'Sin horario definido'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{abierto ? 'Ocultar' : 'Configurar'}</Typography>
                        {abierto ? <ExpandLessIcon sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                      </Box>
                    </Box>

                    {abierto && (
                      <Box sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                          {slots.map((slot, i) => (
                            <FilaHorario
                              key={i}
                              slot={slot}
                              numero={i + 1}
                              onCambiar={(campo, valor) => cambiarSlot(day, i, campo, valor)}
                              onQuitar={() => quitarSlot(day, i)}
                              puedeQuitar
                            />
                          ))}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <Button
                            type="button" variant="outlined" startIcon={<AddIcon />}
                            onClick={() => editarDia(day, s => [...s, horarioVacio()])}
                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                          >
                            Agregar horario
                          </Button>
                          {dias.length > 1 && (
                            <Button
                              type="button" variant="outlined" startIcon={<ContentCopyIcon />}
                              onClick={() => replicarAlSiguiente(day)}
                              sx={{ borderRadius: '8px', textTransform: 'none' }}
                            >
                              Replicar al día siguiente
                            </Button>
                          )}
                          {dias.length > 1 && (
                            <Button
                              type="button" variant="outlined" startIcon={<ContentCopyIcon />}
                              onClick={() => aplicarATodos(day)}
                              sx={{ borderRadius: '8px', textTransform: 'none' }}
                            >
                              Aplicar a todos los días
                            </Button>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      <ConfirmModal
        show={confirmarUnificar}
        message={`Tus días tienen horarios distintos. Si continúas, todos usarán el horario de ${seleccionados[0] || ''} y se reemplazarán los demás.`}
        btnTitle="Usar el mismo horario"
        confirm={unificar}
        cancel={() => setConfirmarUnificar(false)}
      />
    </Box>
  );
};

DisponibilidadSemanal.propTypes = {
  value: PropTypes.arrayOf(PropTypes.shape({
    day: PropTypes.string,
    slots: PropTypes.arrayOf(PropTypes.shape({ inicio: PropTypes.string, fin: PropTypes.string })),
  })),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  onAviso: PropTypes.func,
};

export default DisponibilidadSemanal;
